import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, Mail, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/common/LanguageSelector';

const WaitingForApprovalPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Redirect to homepage if user becomes active
  useEffect(() => {
    if (currentUser && currentUser.is_active === true) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Language Selector */}
        <div className="flex justify-end mb-4">
          <LanguageSelector requireAuth={false} />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {t('waitingApproval.title', { defaultValue: 'Account Pending Approval' })}
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6">
            {t('waitingApproval.description', {
              defaultValue: 'Thank you for registering! Your account is currently pending approval from our administrators.'
            })}
          </p>

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-3">
              {currentUser?.picture && (
                <img
                  src={currentUser.picture}
                  alt={currentUser.displayName || currentUser.email || 'User'}
                  className="w-10 h-10 rounded-full"
                />
              )}
              <div className="text-left">
                {currentUser?.displayName && (
                  <p className="font-medium text-gray-900">{currentUser.displayName}</p>
                )}
                {currentUser?.email && (
                  <p className="text-sm text-gray-600">{currentUser.email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm text-blue-900 font-medium mb-1">
                  {t('waitingApproval.nextSteps', { defaultValue: 'What happens next?' })}
                </p>
                <p className="text-sm text-blue-700">
                  {t('waitingApproval.nextStepsDescription', {
                    defaultValue: 'You will receive an email notification once your account has been approved. '
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Help Text */}
          <p className="text-sm text-gray-500 mb-6">
            {t('waitingApproval.helpText', {
              defaultValue: 'If you have any questions or concerns, please contact our support team.'
            })}
          </p>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 font-medium"
          >
            <LogOut className="w-4 h-4" />
            {t('waitingApproval.logout', { defaultValue: 'Sign Out' })}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            {t('waitingApproval.footer', {
              defaultValue: 'Need help? Contact us at support@example.com'
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaitingForApprovalPage;
